import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { readSportsWalletSummary } from '../../../_services/_helpers/sportsWalletRead';
import { resolveAvailableBalance } from '../_helpers/betCartStakeLimits';

const accountKeyOf = guard => String(guard?.accountKey || (String(guard?.userId ?? '') + ':' + String(guard?.generation ?? '')));
const walletHintValue = summary => {
  const value = summary?.available_balance ?? summary?.balance ?? summary?.wallet?.available_balance ?? summary?.wallet?.balance;
  if (typeof value !== 'number' && typeof value !== 'string') return null;
  if (typeof value === 'string' && !value.trim()) return null;
  const number = Number(typeof value === 'string' ? value.replace(/,/g, '').trim() : value);
  return Number.isFinite(number) && number >= 0 ? number : null;
};

// Shared wallet snapshots only invalidate this account-owned read cache.
// They never establish a balance or an authenticated request carrier.
export default function useSportsAccountBalance({
  currentAccountKey, captureAccountGuard, isAccountGuardCurrent, applyWalletSummary, walletSummary,
}) {
  const [resolvedBalanceRecord, setResolvedBalanceRecord] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const ownerRef = useRef(null);
  useLayoutEffect(() => {
    const owner = { accountKey: currentAccountKey, active: null, revision: 0, value: null, hint: undefined, hintValue: null, trailing: false };
    ownerRef.current = owner;
    setResolvedBalanceRecord(null);
    setBalanceLoading(false);
    return () => { if (ownerRef.current === owner) ownerRef.current = null; };
  }, [currentAccountKey]);
  const owns = useCallback((owner, guard) => (
    owner != null && ownerRef.current === owner && Number(guard?.userId) > 0
    && owner.accountKey === accountKeyOf(guard) && isAccountGuardCurrent(guard)
  ), [isAccountGuardCurrent]);
  const applyAccountWallet = useCallback((payload, accountGuard) => {
    const owner = ownerRef.current;
    if (!payload || !owns(owner, accountGuard)) return null;
    // A newer placement response fences any older read already on the wire.
    owner.revision += 1;
    owner.trailing = false;
    const applied = applyWalletSummary(payload);
    if (!owns(owner, accountGuard)) return null;
    const value = resolveAvailableBalance(applied, payload);
    owner.value = value;
    setResolvedBalanceRecord({ accountKey: owner.accountKey, value });
    return value;
  }, [applyWalletSummary, owns]);
  const refreshAvailableBalance = useCallback((accountGuard = captureAccountGuard()) => {
    const owner = ownerRef.current;
    if (!owns(owner, accountGuard)) return Promise.resolve(null);
    if (owner.active) return owner.active.promise;
    const request = { revision: owner.revision, hintValue: owner.hintValue, promise: null };
    owner.active = request;
    setBalanceLoading(true);
    request.promise = (async () => {
      try {
        const payload = await readSportsWalletSummary({
          accountGuard, isCurrent: () => owns(owner, accountGuard),
        });
        if (!owns(owner, accountGuard) || owner.revision !== request.revision || owner.trailing) return null;
        return applyAccountWallet(payload, accountGuard);
      } catch {
        // Keep the last verified same-account value. A later external snapshot
        // may retry even when its amount equals the failed hint; no error loop.
        return null;
      } finally {
        if (owns(owner, accountGuard) && owner.active === request) {
          owner.active = null;
          setBalanceLoading(false);
          const trailing = owner.trailing;
          owner.trailing = false;
          if (trailing) void refreshAvailableBalance(accountGuard);
        }
      }
    })();
    return request.promise;
  }, [applyAccountWallet, captureAccountGuard, owns]);
  useEffect(() => {
    const owner = ownerRef.current;
    if (!owner || owner.hint === walletSummary) return;
    owner.hint = walletSummary;
    const value = walletHintValue(walletSummary);
    owner.hintValue = value;
    if (value == null || value === owner.value) return;
    const guard = captureAccountGuard();
    if (!owns(owner, guard)) return;
    if (owner.active) {
      owner.trailing = value !== owner.active.hintValue;
      return;
    }
    void refreshAvailableBalance(guard);
  }, [walletSummary, currentAccountKey, captureAccountGuard, owns, refreshAvailableBalance]);
  return {
    availableBalance: resolvedBalanceRecord?.accountKey === currentAccountKey
      ? resolveAvailableBalance(resolvedBalanceRecord.value) : 0,
    resolvedBalanceRecord, setResolvedBalanceRecord,
    balanceLoading, setBalanceLoading, applyAccountWallet, refreshAvailableBalance,
  };
}
